import React, { useState, useEffect } from 'react';

import { PriorityHigh } from '@mui/icons-material';
import {
  RadioGroup,
  FormLabel,
  Radio,
  Modal,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import WelcomeSidebar from '../../components/WelcomeSidebar/WelcomeSidebar';
import { getPublicMerchantInfo } from '../../store/modules/merchant/api';

const OnboardingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [testMode, setTestMode] = useState(true);

  const onRadioChange = () => {
    setModalOpen(true);
  };

  useEffect(() => {
    const getTestMode = async () => {
      const params = new URLSearchParams(window.location.search);
      const merchantId = params.get('merchantId');
      if (merchantId) {
        // Since this is a standalone page, separate from the main app, we are not utilizing the persisted state
        try {
          const merchantPublicInfo = await getPublicMerchantInfo(merchantId);
          setTestMode(!!merchantPublicInfo.checkoutTestMode);
        } catch {
          // Fail silently
        }
      }
    };
    getTestMode();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100%',
        justifyContent: 'flex-start',
      }}
    >
      <WelcomeSidebar />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: 22,
          pl: 25,
          pb: 10,
          alignItems: 'flex-start',
          overflow: 'auto',
          maxWidth: '560px',
        }}
      >
        <Typography sx={{ pt: 0.5 }} variant="xxl" fontWeight="semibold">
          Application information
        </Typography>
        <Chip
          sx={{ mt: 2, backgroundColor: 'grey.200', borderRadius: '8px' }}
          label={
            <span>
              <PriorityHigh sx={{ fontSize: 18, verticalAlign: 'middle' }} /> Your application is
              currently in <b>{testMode ? 'Test' : 'Live'}</b> mode
            </span>
          }
        />
        <Typography sx={{ fontSize: 14, color: 'grey.900', mt: 2 }}>
          {testMode
            ? 'Skipify will only be running on your store for email addresses you have whitelisted.'
            : 'The application is turned on and is running for anyone visiting your store'}
        </Typography>
        <FormControl sx={{ mt: 3 }}>
          <FormLabel id="demo-radio-buttons-group-label">Please select a mode:</FormLabel>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue="female"
            name="radio-buttons-group"
            onChange={onRadioChange}
          >
            <FormControlLabel
              value="Test"
              control={<Radio checked={testMode} />}
              label="Test"
              checked={testMode}
            />
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
              When Test mode is enabled, the application is turned off for your store and will only
              run for whitelisted emails.
            </Typography>
            <FormControlLabel
              value="Live"
              control={<Radio checked={!testMode} />}
              label="Live"
              checked={!testMode}
            />
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
              When Live mode is enabled, the application is turned on and is running for anyone
              visiting your store. Please work with our{' '}
              <Link sx={{ fontSize: 14, fontWeight: 600 }} href="mailto:support@skipify.com">
                implementation team
              </Link>{' '}
              prior to taking your store Live.
            </Typography>
          </RadioGroup>
        </FormControl>
      </Box>
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        aria-labelledby="skipify implementation info modal"
        aria-describedby="skipify implementation info modal"
      >
        <Box
          sx={{
            width: '360px',
            background: 'rgb(255, 255, 255)',
            borderRadius: '8px',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Typography variant="xl" fontWeight="semibold">
            Turn {testMode ? 'on' : 'off'} your Skipify application
          </Typography>
          <Typography sx={{ mt: 2 }}>
            {testMode
              ? 'When Live mode is enabled, the application is turned on and is running for anyone visiting your store'
              : 'When Test mode is enabled, the application is turned off for your store and will only run for whitelisted emails'}
            . Please contact our{' '}
            <Link sx={{ fontSize: 14, fontWeight: 600 }} href="mailto:support@skipify.com">
              implementation team
            </Link>{' '}
            to {testMode ? 'enable' : 'disable'} live mode.
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default OnboardingPage;
